import qrcode from "qrcode-generator";
import cardArt from "./giftpass-card.jpg";
import { CARDS_PER_PAGE, type GiftpassOrder } from "./giftpass-orders.model";

// The print sheets for physical GiftPass orders (G4). One window, straight to
// the printer: per order a cover sheet (code, buyer, address, card count),
// then its cards, two across and three down on A4 landscape, with cut lines.
//
// The card is "2026 AUG - GP DESIGN" (5 × 2.57 in, rendered at 300 dpi to
// giftpass-card.jpg). Its grey square, bottom right, holds a white tile with
// the QR — it opens Claim GiftPass in the client app (Leon, 2026-10-26). The
// code is printed in the gold card under "giftpass.bednbody.ph", so the pass
// can be typed in instead.

/** Where a card's QR sends the holder: the claim screen in the client app. */
export function claimUrl(code: string): string {
  return `https://app.bednbody.ph/claim?code=${encodeURIComponent(code)}`;
}

export function qrSvg(text: string): string {
  const qr = qrcode(0, "M");
  qr.addData(text);
  qr.make();
  return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });
}

const escape = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c,
  );

export interface PrintJob {
  order: Pick<
    GiftpassOrder,
    "order_code" | "buyer_name" | "delivery_address" | "package_name" | "quantity"
  >;
  codes: string[];
}

function chunk<T>(list: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

export function printSheetHtml(jobs: PrintJob[], artUrl: string): string {
  const pages = jobs.flatMap(({ order, codes }) => [
    `<section class="page cover">
      <p class="small">GiftPass order</p>
      <h1>${escape(order.order_code)}</h1>
      <p class="big">${escape(order.buyer_name)}</p>
      <p>${escape(order.delivery_address ?? "")}</p>
      <p>${escape(order.package_name)}${order.quantity > 1 ? ` × ${order.quantity}` : ""} · ${codes.length} cards</p>
    </section>`,
    ...chunk(codes, CARDS_PER_PAGE).map(
      (page) =>
        `<section class="page cards">${page
          .map(
            (code) => `<article class="card">
            <img class="art" src="${escape(artUrl)}" alt="">
            <div class="tile">${qrSvg(claimUrl(code))}</div>
            <p class="code">${escape(code)}</p>
          </article>`,
          )
          .join("")}</section>`,
    ),
  ]);

  return `<!doctype html><html lang="en"><head><meta charset="utf-8">
<title>GiftPass cards</title>
<style>
@page { size: A4 landscape; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; font-family: Geist, system-ui, sans-serif; color: #0f3d33; }
.page { width: 297mm; height: 210mm; page-break-after: always; overflow: hidden; }
.cover { padding: 30mm; display: flex; flex-direction: column; gap: 4mm; }
.cover h1 { margin: 0; font: 600 28pt ui-monospace, monospace; }
.cover p { margin: 0; font-size: 14pt; }
.cover .big { font-size: 20pt; font-weight: 600; }
.cover .small { font-size: 11pt; color: #3d5a52; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.cards { display: grid; grid-template-columns: 127mm 127mm; grid-auto-rows: 65.35mm;
  justify-content: center; align-content: center; }
.card { position: relative; overflow: hidden; outline: 0.2mm dashed #9bb3ab; }
.art { position: absolute; inset: 0; width: 100%; height: 100%; }
/* The design's grey square: 82.1% across, 66.3% down, 15.4% × 29.9% of the card. */
.tile { position: absolute; left: 82.1%; top: 66.3%; width: 15.4%; height: 29.9%;
  background: #ffffff; border-radius: 1mm; padding: 1.2mm; }
.tile svg { display: block; width: 100%; height: 100%; }
/* Under "giftpass.bednbody.ph" in the gold card, bottom left (Leon, 2026-10-26). */
.code { position: absolute; left: 5.3%; top: 90.6%; margin: 0; color: #4a3b12;
  font: 600 7pt ui-monospace, monospace; letter-spacing: 0.04em; white-space: nowrap; }
</style></head><body>${pages.join("")}</body></html>`;
}

/** Opens the sheets in a new window and its print dialog. */
export function printCards(jobs: PrintJob[]): boolean {
  const win = window.open("", "_blank");
  if (!win) return false;
  // Every value from an order is escaped in printSheetHtml; the QR is our SVG.
  win.document.open();
  win.document.write(printSheetHtml(jobs, new URL(cardArt, window.location.href).href));
  win.document.close();
  // Print only once every card's art has loaded, or the first pages print blank.
  void Promise.all(
    Array.from(win.document.images, (img) => img.decode().catch(() => undefined)),
  ).then(() => {
    win.focus();
    win.print();
  });
  return true;
}
